import { FC, useMemo, useState } from 'react';
import { Button, Dropdown } from 'antd';
import { TbChevronDown, TbPlus, TbSitemap, TbUsers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { usePlanUsageLimits } from '@/hooks/usePlanUsageLimits';
import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    EmptySection,
    HierarchyTreeRow,
    LearnMoreFooter,
    TWO_ROW_DROPDOWN_HIERARCHY_GEOMETRY,
} from '@/shared';

import CreateSubteamModal from '../../../../components/CreateSubteamModal';
import type { TeamCreationTarget } from '../../../../components/CreateSubteamModal';

interface TeamCreationDropdownProps {
    teams: TeamCreationTarget[];
}

interface FilteredTeam {
    subteams: NonNullable<TeamCreationTarget['subteams']>;
    team: TeamCreationTarget;
}

const TeamCreationDropdown: FC<TeamCreationDropdownProps> = ({ teams }) => {
    const { token } = useAntDesignTheme();
    const { getUsageState } = usePlanUsageLimits();
    const [open, setOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [selectedTeam, setSelectedTeam] = useState<TeamCreationTarget>();

    const filteredTeams = useMemo<FilteredTeam[]>(() => {
        const query = searchInput.trim().toLocaleLowerCase();

        return teams.flatMap((team) => {
            const subteams = team.subteams ?? [];
            if (!query || team.name.toLocaleLowerCase().includes(query)) {
                return [{ subteams, team }];
            }

            const matchingSubteams = subteams.filter((subteam) => subteam.name.toLocaleLowerCase().includes(query));

            return matchingSubteams.length ? [{ subteams: matchingSubteams, team }] : [];
        });
    }, [searchInput, teams]);

    const openCreateSubteam = (team: TeamCreationTarget) => {
        setOpen(false);
        setSelectedTeam(team);
    };

    return (
        <>
            <Dropdown
                destroyOnHidden
                open={open}
                placement='bottomRight'
                popupRender={() => (
                    <DropdownPanel
                        footer={<LearnMoreFooter topic='teams and subteams' variant='dropdown' />}
                        maxHeight='min(28rem, 70vh)'
                        search={{
                            ariaLabel: 'Search teams and subteams',
                            placeholder: 'Search Teams...',
                            setValue: setSearchInput,
                            value: searchInput,
                        }}
                        width='21rem'
                    >
                        <DropdownMenuLabel icon={<TbUsers size='0.875rem' />}>Teams</DropdownMenuLabel>
                        {filteredTeams.length ? (
                            <div aria-label='Teams and subteams' role='list' style={{ paddingBottom: '0.25rem' }}>
                                {filteredTeams.map(({ subteams, team }) => {
                                    const subteamUsage = getUsageState(
                                        'subteamsPerTeam',
                                        team.subteamCount ?? team.subteams?.length ?? 0,
                                    );
                                    const childRowCount = subteams.length + 1;

                                    return (
                                        <div key={team.id} role='listitem'>
                                            <HierarchyTreeRow depth={0}>
                                                <div
                                                    style={{
                                                        borderTop: `1px solid ${token.colorBorderSecondary}`,
                                                        padding: '0.375rem 0.75rem',
                                                    }}
                                                >
                                                    <DropdownOptionContent
                                                        avatar={{ icon: <TbUsers size='0.875rem' /> }}
                                                        description={`${team.subteamCount ?? team.subteams?.length ?? 0} subteams`}
                                                        title={team.name}
                                                    />
                                                </div>
                                            </HierarchyTreeRow>
                                            {subteams.map((subteam, index) => (
                                                <HierarchyTreeRow
                                                    {...TWO_ROW_DROPDOWN_HIERARCHY_GEOMETRY}
                                                    continueToSiblings={index < childRowCount - 1}
                                                    depth={1}
                                                    firstSibling={index === 0}
                                                    key={subteam.id}
                                                >
                                                    <div style={{ padding: '0.25rem 0.75rem' }}>
                                                        <DropdownOptionContent
                                                            avatar={{ icon: <TbSitemap size='0.75rem' /> }}
                                                            description={`${subteam.members?.length ?? 0} members`}
                                                            title={subteam.name}
                                                        />
                                                    </div>
                                                </HierarchyTreeRow>
                                            ))}
                                            <HierarchyTreeRow
                                                {...TWO_ROW_DROPDOWN_HIERARCHY_GEOMETRY}
                                                depth={1}
                                                firstSibling={subteams.length === 0}
                                            >
                                                <Button
                                                    block
                                                    disabled={subteamUsage.reached}
                                                    icon={<TbPlus size='0.875rem' />}
                                                    title={subteamUsage.reached ? subteamUsage.reason : undefined}
                                                    type='text'
                                                    style={{
                                                        color: token.colorText,
                                                        height: 'auto',
                                                        justifyContent: 'flex-start',
                                                        paddingBlock: '0.375rem',
                                                    }}
                                                    onClick={() => openCreateSubteam(team)}
                                                >
                                                    New Subteam
                                                </Button>
                                            </HierarchyTreeRow>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptySection
                                description='Try another team or subteam name.'
                                height='9rem'
                                icon={<TbSitemap size='1.125rem' />}
                                label='No Teams Found'
                            />
                        )}
                    </DropdownPanel>
                )}
                trigger={['click']}
                onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    if (!nextOpen) {
                        setSearchInput('');
                    }
                }}
            >
                <Button
                    aria-label='Open team creation options'
                    icon={<TbChevronDown size='0.875rem' />}
                    type='primary'
                />
            </Dropdown>
            <CreateSubteamModal
                open={Boolean(selectedTeam)}
                team={selectedTeam}
                onClose={() => setSelectedTeam(undefined)}
            />
        </>
    );
};

export default TeamCreationDropdown;
